/* ================================================= */
/* PASTORAL SCHEDULING ENGINE                        */
/* Pure intelligence — no calendar, no mutations     */
/*                                                   */
/* Transforms active workflows into:                 */
/* - A prioritized weekly action list                */
/* - A pastoral load assessment                      */
/* - A contextual weekly focus                       */
/* - Grouped zone suggestions (same church = 1 trip) */
/*                                                   */
/* Designed for extension into:                      */
/* - AI scheduling assistant                         */
/* - Suggested pastoral routes                       */
/* - Smart district coordination                     */
/* - Auto-generated weekly plans                     */
/* - Ministry balancing                              */
/* ================================================= */

import { WORKFLOW_URGENCY, WORKFLOW_STAGES, WORKFLOW_TYPES } from "./pastoral-workflows";

/* ============================= */
/* SCHEDULE WINDOWS               */
/* ============================= */

export const SCHEDULE_WINDOW = {
  THIS_WEEK: "this_week",
  NEXT_WEEK: "next_week",
  UPCOMING:  "upcoming",
};

/* ============================= */
/* LOAD LEVELS                   */
/* ============================= */

export const LOAD_LEVEL = {
  LIGHT:      "light",
  MODERATE:   "moderate",
  HEAVY:      "heavy",
  OVERLOADED: "overloaded",
};

/* ============================= */
/* FOCUS TONES                   */
/* ============================= */

export const FOCUS_TONE = {
  URGENT:    "urgent",
  NURTURING: "nurturing",
  BALANCED:  "balanced",
  HEALTHY:   "healthy",
};

/* ============================= */
/* INTERNAL WEIGHTS              */
/* Single source of truth for    */
/* priority + load computation   */
/* ============================= */

const URGENCY_PRIORITY_SCORE = { critical: 100, high: 60, medium: 30, low: 10 };
const URGENCY_LOAD_COST      = { critical: 25,  high: 15, medium: 8,  low: 3  };

const STAGE_PRIORITY_BONUS = {
  [WORKFLOW_STAGES.STALLED]:    25,   // stalled = higher scheduling need
  [WORKFLOW_STAGES.INITIATED]:  5,
  [WORKFLOW_STAGES.ACTIVE]:     0,
  [WORKFLOW_STAGES.RECOVERING]: -5,   // recovering = keep gentle, not forced
  [WORKFLOW_STAGES.DORMANT]:    -15,
  [WORKFLOW_STAGES.SUSTAINED]:  -30,  // sustained = don't disturb healthy rhythm
};

const URGENCY_TO_WINDOW = {
  [WORKFLOW_URGENCY.CRITICAL]: SCHEDULE_WINDOW.THIS_WEEK,
  [WORKFLOW_URGENCY.HIGH]:     SCHEDULE_WINDOW.THIS_WEEK,
  [WORKFLOW_URGENCY.MEDIUM]:   SCHEDULE_WINDOW.NEXT_WEEK,
  [WORKFLOW_URGENCY.LOW]:      SCHEDULE_WINDOW.UPCOMING,
};

/* ============================= */
/* FOCUS MESSAGE MAPS             */
/* Static — no interpolation     */
/* ============================= */

const FOCUS_BY_WORKFLOW_TYPE = {
  [WORKFLOW_TYPES.CRISIS_RESPONSE]: {
    headline: "Esta semana hay situaciones críticas que requieren respuesta pastoral",
    tone:     FOCUS_TONE.URGENT,
  },
  [WORKFLOW_TYPES.CHURCH_RECOVERY]: {
    headline: "Esta semana hay iglesias que requieren atención pastoral",
    tone:     FOCUS_TONE.URGENT,
  },
  [WORKFLOW_TYPES.VISIT_FOLLOWUP]: {
    headline: "Esta semana conviene priorizar el seguimiento pastoral de visitas",
    tone:     FOCUS_TONE.NURTURING,
  },
  [WORKFLOW_TYPES.RECOVERY_PATH]: {
    headline: "Esta semana es clave sostener el proceso de recuperación iniciado",
    tone:     FOCUS_TONE.NURTURING,
  },
  [WORKFLOW_TYPES.SPIRITUAL_SUPPORT]: {
    headline: "Esta semana se recomienda acompañamiento espiritual sostenido",
    tone:     FOCUS_TONE.NURTURING,
  },
  [WORKFLOW_TYPES.PRAYER_ESCALATION]: {
    headline: "Esta semana conviene atender las peticiones de oración activas",
    tone:     FOCUS_TONE.BALANCED,
  },
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]: {
    headline: "Esta semana hay candidatos al bautismo que requieren seguimiento",
    tone:     FOCUS_TONE.NURTURING,
  },
};

const FOCUS_BY_LOAD = {
  [LOAD_LEVEL.LIGHT]: {
    headline: "La semana está tranquila — buen momento para visitas preventivas",
    tone:     FOCUS_TONE.HEALTHY,
  },
  [LOAD_LEVEL.MODERATE]: {
    headline: "Semana con carga pastoral moderada — mantener el ritmo",
    tone:     FOCUS_TONE.BALANCED,
  },
  [LOAD_LEVEL.HEAVY]: {
    headline: "Semana exigente — priorizar lo crítico y distribuir lo que se pueda",
    tone:     FOCUS_TONE.URGENT,
  },
  [LOAD_LEVEL.OVERLOADED]: {
    headline: "La carga pastoral esta semana está al límite — enfocarse en lo urgente",
    tone:     FOCUS_TONE.URGENT,
  },
};

const LOAD_CONTEXT_SUFFIX = {
  [LOAD_LEVEL.LIGHT]:      "El ritmo actual permite avanzar en acompañamiento preventivo",
  [LOAD_LEVEL.MODERATE]:   "El ritmo es sostenible — seguir con constancia pastoral",
  [LOAD_LEVEL.HEAVY]:      "Considera distribuir la carga con otros líderes o postergarlo a la próxima semana",
  [LOAD_LEVEL.OVERLOADED]: "Es importante no intentar atender todo — seleccionar lo más urgente primero",
};

const WORKFLOW_SUGGESTION_TEXT = {
  [WORKFLOW_TYPES.CRISIS_RESPONSE]:    "Visitar familias con oración urgente",
  [WORKFLOW_TYPES.CHURCH_RECOVERY]:    "Programar visita pastoral a iglesias críticas",
  [WORKFLOW_TYPES.VISIT_FOLLOWUP]:     "Retomar el seguimiento de visitas estancadas",
  [WORKFLOW_TYPES.RECOVERY_PATH]:      "Sostener el ritmo de recuperación iniciado",
  [WORKFLOW_TYPES.SPIRITUAL_SUPPORT]:  "Profundizar el acompañamiento espiritual",
  [WORKFLOW_TYPES.PRAYER_ESCALATION]:  "Interceder con las peticiones urgentes activas",
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]:"Dar continuidad al proceso bautismal",
};

/* ============================= */
/* COMPUTE SCHEDULING PRIORITY   */
/* ============================= */

/**
 * Scores a workflow for scheduling priority.
 * Higher score = schedule sooner.
 *
 * Considers: urgency weight + lifecycle stage + recency + continuity gap.
 * Not just urgency — a recovering workflow with good continuity
 * scores lower than a stalled one even at the same urgency level.
 *
 * @param {Workflow} workflow
 * @returns {number} priority score (0–200)
 */
export function computeSchedulingPriority(workflow) {
  const urgencyScore      = URGENCY_PRIORITY_SCORE[workflow.urgency]      ?? 0;
  const stageBonus        = STAGE_PRIORITY_BONUS[workflow.stage]          ?? 0;
  const recencyScore      = workflow.daysSinceLastAction !== null
    ? Math.min(30, Math.round(workflow.daysSinceLastAction * 0.25))
    : 20; // unknown = treat as moderate gap
  const continuityPenalty = Math.round(
    (100 - Math.max(0, workflow.continuityScore ?? 50)) * 0.25
  );

  return Math.min(200, urgencyScore + stageBonus + recencyScore + continuityPenalty);
}

/* ============================= */
/* ESTIMATE PASTORAL LOAD        */
/* ============================= */

/**
 * Estimates aggregate pastoral load from a workflow list.
 * Returns a contextual load object — not hours, but pastoral weight.
 *
 * Load semantics:
 *   light (0–27):      Semana tranquila
 *   moderate (28–54):  Carga sostenible
 *   heavy (55–79):     Semana exigente
 *   overloaded (80+):  Carga al límite
 *
 * Designed as the input for:
 * - WeeklyFocusCard tone selection
 * - AI overload prevention
 * - Smart scheduling: defer medium items when heavy
 *
 * @param {Workflow[]} workflows
 * @returns {PastoralLoad}
 */
export function estimatePastoralLoad(workflows) {
  if (!workflows || workflows.length === 0) {
    return { total: 0, level: LOAD_LEVEL.LIGHT, label: "Semana tranquila", critical: 0, high: 0, medium: 0, low: 0 };
  }

  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  for (const w of workflows) {
    const key = w.urgency in counts ? w.urgency : "low";
    counts[key]++;
  }

  const total = Math.min(100, workflows.reduce(
    (sum, w) => sum + (URGENCY_LOAD_COST[w.urgency] ?? 0), 0
  ));

  const level = total >= 80 ? LOAD_LEVEL.OVERLOADED
              : total >= 55 ? LOAD_LEVEL.HEAVY
              : total >= 28 ? LOAD_LEVEL.MODERATE
              :               LOAD_LEVEL.LIGHT;

  const LEVEL_LABELS = {
    [LOAD_LEVEL.LIGHT]:      "Semana tranquila",
    [LOAD_LEVEL.MODERATE]:   "Carga moderada",
    [LOAD_LEVEL.HEAVY]:      "Semana exigente",
    [LOAD_LEVEL.OVERLOADED]: "Carga al límite",
  };

  return { total, level, label: LEVEL_LABELS[level], ...counts };
}

/* ============================= */
/* BUILD WEEKLY FOCUS             */
/* ============================= */

/**
 * Synthesizes a human-readable weekly pastoral focus from active workflows + load.
 *
 * Focus shape: { headline, context, tone, suggestions[] }
 * tone: "urgent" | "nurturing" | "balanced" | "healthy"
 *
 * Priority: overload > critical workflows > dominant workflow type > load level
 *
 * Designed for WeeklyFocusCard and future AI assistant briefings.
 *
 * @param {Workflow[]} workflows
 * @param {PastoralLoad} load
 * @returns {WeeklyFocus}
 */
export function buildWeeklyFocus(workflows, load) {
  const active = (workflows || []).filter(
    (w) => w.stage !== WORKFLOW_STAGES.DORMANT && w.stage !== WORKFLOW_STAGES.SUSTAINED
  );

  if (active.length === 0) {
    return {
      headline:    FOCUS_BY_LOAD[LOAD_LEVEL.LIGHT].headline,
      context:     LOAD_CONTEXT_SUFFIX[LOAD_LEVEL.LIGHT],
      tone:        FOCUS_TONE.HEALTHY,
      suggestions: [],
    };
  }

  // Heavy/overloaded load takes precedence over workflow-type focus
  const lvl = load?.level ?? LOAD_LEVEL.LIGHT;
  if (lvl === LOAD_LEVEL.OVERLOADED || lvl === LOAD_LEVEL.HEAVY) {
    return {
      headline:    FOCUS_BY_LOAD[lvl].headline,
      context:     LOAD_CONTEXT_SUFFIX[lvl],
      tone:        FOCUS_BY_LOAD[lvl].tone,
      suggestions: buildTopSuggestions(active),
    };
  }

  // Otherwise: derive from the dominant (highest-urgency) workflow type
  const dominant  = active[0]; // pre-sorted by urgency
  const focusMsg  = FOCUS_BY_WORKFLOW_TYPE[dominant.type] ?? FOCUS_BY_LOAD[lvl];

  // Build a contextual count sentence
  const criticalCount = active.filter((w) => w.urgency === WORKFLOW_URGENCY.CRITICAL).length;
  const context = criticalCount > 0
    ? `${criticalCount} situacion${criticalCount !== 1 ? "es" : ""} crítica${criticalCount !== 1 ? "s" : ""} requieren atención esta semana`
    : `${active.length} proceso${active.length !== 1 ? "s" : ""} pastoral${active.length !== 1 ? "es" : ""} activo${active.length !== 1 ? "s" : ""} en seguimiento`;

  return {
    headline:    focusMsg.headline,
    context,
    tone:        focusMsg.tone,
    suggestions: buildTopSuggestions(active),
  };
}

/* Returns up to 3 deduplicated action suggestions from active workflows */
function buildTopSuggestions(workflows) {
  const seen       = new Set();
  const suggestions = [];
  for (const w of workflows) {
    const text = WORKFLOW_SUGGESTION_TEXT[w.type];
    if (text && !seen.has(w.type)) {
      seen.add(w.type);
      suggestions.push(text);
    }
    if (suggestions.length >= 3) break;
  }
  return suggestions;
}

/* ============================= */
/* GROUP BY ZONE                  */
/* Same church = potentially     */
/* one pastoral trip             */
/* ============================= */

/**
 * Groups schedule items by church (zone) for intelligent visit batching.
 * Visiting 3 families from the same congregation → one pastoral trip.
 *
 * Returns: Array<{ key, label, items }> sorted by highest-priority item per zone.
 *
 * Compatible with MiniTimelineCard groups prop for future display.
 * Ready for AI route optimization (travel efficiency × pastoral priority).
 *
 * @param {ScheduleItem[]} scheduleItems
 * @returns {ZoneGroup[]}
 */
export function groupByZone(scheduleItems) {
  const zones = {};

  for (const item of scheduleItems) {
    const key = item.groupKey ?? "district";
    if (!zones[key]) {
      zones[key] = {
        key,
        label: item.churchName ?? "Distrito",
        items: [],
      };
    }
    zones[key].items.push(item);
  }

  return Object.values(zones).sort((a, b) => {
    const maxA = Math.max(...a.items.map((i) => i.priority ?? 0));
    const maxB = Math.max(...b.items.map((i) => i.priority ?? 0));
    return maxB - maxA;
  });
}

/* ============================= */
/* DETECT SCHEDULING CONFLICTS   */
/* ============================= */

/**
 * Identifies pastoral scheduling tensions.
 * NOT calendar conflicts — contextual pastoral load conflicts.
 *
 * Returns: Array<{ type, message, suggestion }>
 * Ready for: AI overload prevention, district assistant warnings.
 *
 * @param {PastoralLoad} load
 * @param {Workflow[]}   workflows
 * @returns {ConflictWarning[]}
 */
export function detectSchedulingConflicts(load, workflows = []) {
  const conflicts = [];

  if (load.level === LOAD_LEVEL.OVERLOADED) {
    conflicts.push({
      type:       "overload",
      message:    "La carga pastoral supera lo sostenible esta semana",
      suggestion: "Considera delegar visitas de nivel medio a líderes locales",
    });
  }

  const criticalCount = workflows.filter(
    (w) => w.urgency === WORKFLOW_URGENCY.CRITICAL
  ).length;
  if (criticalCount >= 3) {
    conflicts.push({
      type:       "critical_cluster",
      message:    `Hay ${criticalCount} situaciones críticas simultáneas`,
      suggestion: "Priorizar las más antiguas o las que tengan oración urgente activa",
    });
  }

  const stalledCount = workflows.filter(
    (w) => w.stage === WORKFLOW_STAGES.STALLED
  ).length;
  if (stalledCount >= 4) {
    conflicts.push({
      type:       "stall_cluster",
      message:    `${stalledCount} procesos pastorales están estancados a la vez`,
      suggestion: "Elegir los 2-3 más urgentes y postergar el resto para la próxima semana",
    });
  }

  return conflicts;
}

/* ============================= */
/* BUILD SUGGESTED SCHEDULE      */
/* Main entry point              */
/* ============================= */

/**
 * Converts district workflows + urgent prayers into a prioritized pastoral action list.
 * Sorts by scheduling priority score, assigns temporal windows (this week / next week).
 *
 * Output is a flat normalized list ready for:
 * - groupByZone() for zone batching
 * - Direct rendering in MiniTimelineCard (as items with priority)
 * - AI weekly plan generation
 * - Smart district coordination
 *
 * @param {Workflow[]} districtWorkflows  — from buildDistrictWorkflows()
 * @param {Object[]}  prayers            — active prayers for urgent cluster detection
 * @returns {ScheduleItem[]}
 */
export function buildSuggestedSchedule(districtWorkflows = [], prayers = []) {
  const items = [];

  // Church-level schedule items from district workflows
  for (const wf of districtWorkflows) {
    const priority = computeSchedulingPriority(wf);
    const window   = URGENCY_TO_WINDOW[wf.urgency] ?? SCHEDULE_WINDOW.UPCOMING;

    items.push({
      id:         `schedule-${wf.type}-${wf.entityId ?? "district"}`,
      type:       "visit",
      entityType: wf.entityType,
      entityId:   wf.entityId,
      entityName: wf.meta?.churchName ?? "Iglesia",
      churchId:   wf.entityType === "church" ? wf.entityId : null,
      churchName: wf.meta?.churchName ?? null,
      urgency:    wf.urgency,
      window,
      reason:     wf.nextSuggestedAction,
      priority,
      route:      wf.entityId ? `/churches/${wf.entityId}` : "/churches",
      groupKey:   wf.entityId ?? "district",
    });
  }

  // Urgent prayer cluster — always "this week"
  const urgentPrayers = prayers.filter(
    (p) => p.priority === "alta" && p.status === "activa"
  );
  if (urgentPrayers.length > 0) {
    items.push({
      id:         "schedule-prayers-urgent",
      type:       "prayer",
      entityType: "district",
      entityId:   null,
      entityName: `${urgentPrayers.length} oracion${urgentPrayers.length !== 1 ? "es" : ""} urgente${urgentPrayers.length !== 1 ? "s" : ""}`,
      churchId:   null,
      churchName: null,
      urgency:    WORKFLOW_URGENCY.CRITICAL,
      window:     SCHEDULE_WINDOW.THIS_WEEK,
      reason:     "Interceder con las peticiones urgentes del distrito",
      priority:   120,
      route:      "/prayers",
      groupKey:   "district",
    });
  }

  // Sort by priority descending (most urgent to schedule first)
  items.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  return items;
}
